import { supabase } from '@/lib/supabase';
import type {
  ProjectCustomer,
  ProjectCustomerView,
  ProjectCustomerRole,
  UpdateProjectCustomerInput,
} from '../types';

/**
 * Fetch all customers for a project using the v_project_customers view
 */
export async function fetchProjectCustomers(
  projectId: string
): Promise<ProjectCustomerView[]> {
  const { data, error } = await supabase
    .from('v_project_customers')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[projectCustomersApi] Error fetching project customers:', error);
    throw new Error(error.message);
  }

  // Sort by role priority: primary first, then billing, then others
  const rolePriority: Record<ProjectCustomerRole, number> = {
    primary: 0,
    billing: 1,
    contact: 2,
    stakeholder: 3,
  };

  return (data || []).sort((a, b) => {
    const priorityA = rolePriority[a.role as ProjectCustomerRole] ?? 99;
    const priorityB = rolePriority[b.role as ProjectCustomerRole] ?? 99;
    return priorityA - priorityB;
  });
}

/**
 * Add a customer to a project using the database function
 */
export async function addCustomerToProject(
  projectId: string,
  customerId: string,
  role: ProjectCustomerRole = 'contact',
  isBilling: boolean = false,
  notes?: string
): Promise<string> {
  const { data, error } = await supabase.rpc('add_customer_to_project', {
    p_project_id: projectId,
    p_customer_id: customerId,
    p_role: role,
    p_is_billing: isBilling,
    p_notes: notes || null,
  });

  if (error) {
    console.error('[projectCustomersApi] Error adding customer to project:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update a project customer's role or billing status
 */
export async function updateProjectCustomer(
  projectCustomerId: string,
  input: UpdateProjectCustomerInput
): Promise<ProjectCustomer> {
  // Build update object, handling the is_billing special case
  const updateData: Record<string, unknown> = {};

  if (input.role !== undefined) {
    updateData.role = input.role;
  }

  if (input.notes !== undefined) {
    updateData.notes = input.notes;
  }

  // If setting is_billing to true, we need to clear it from other customers first
  if (input.is_billing === true) {
    // Get the project_id first
    const { data: currentData, error: fetchError } = await supabase
      .from('project_customers')
      .select('project_id')
      .eq('id', projectCustomerId)
      .single();

    if (fetchError) {
      console.error('[projectCustomersApi] Error fetching project customer:', fetchError);
      throw new Error(fetchError.message);
    }

    // Clear is_billing from other customers on this project
    const { error: clearError } = await supabase
      .from('project_customers')
      .update({ is_billing: false })
      .eq('project_id', currentData.project_id)
      .eq('is_billing', true);

    if (clearError) {
      console.error('[projectCustomersApi] Error clearing billing flag:', clearError);
      throw new Error(clearError.message);
    }
  }

  if (input.is_billing !== undefined) {
    updateData.is_billing = input.is_billing;
  }

  const { data, error } = await supabase
    .from('project_customers')
    .update(updateData)
    .eq('id', projectCustomerId)
    .select()
    .single();

  if (error) {
    console.error('[projectCustomersApi] Error updating project customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Set a customer as the primary contact for a project
 */
export async function setProjectPrimaryCustomer(
  projectId: string,
  projectCustomerId: string
): Promise<void> {
  // First, demote the current primary to 'contact'
  const { error: demoteError } = await supabase
    .from('project_customers')
    .update({ role: 'contact' })
    .eq('project_id', projectId)
    .eq('role', 'primary');

  if (demoteError) {
    console.error('[projectCustomersApi] Error demoting current primary:', demoteError);
    throw new Error(demoteError.message);
  }

  // Then, promote the new primary
  const { error: promoteError } = await supabase
    .from('project_customers')
    .update({ role: 'primary' })
    .eq('id', projectCustomerId);

  if (promoteError) {
    console.error('[projectCustomersApi] Error promoting new primary:', promoteError);
    throw new Error(promoteError.message);
  }
}

/**
 * Set a customer as the billing contact for a project
 */
export async function setProjectBillingCustomer(
  projectId: string,
  projectCustomerId: string
): Promise<void> {
  // First, clear billing flag from all customers on this project
  const { error: clearError } = await supabase
    .from('project_customers')
    .update({ is_billing: false })
    .eq('project_id', projectId);

  if (clearError) {
    console.error('[projectCustomersApi] Error clearing billing flags:', clearError);
    throw new Error(clearError.message);
  }

  // Then, set the new billing customer
  const { error: setError } = await supabase
    .from('project_customers')
    .update({ is_billing: true })
    .eq('id', projectCustomerId);

  if (setError) {
    console.error('[projectCustomersApi] Error setting billing customer:', setError);
    throw new Error(setError.message);
  }
}

/**
 * Remove a customer from a project using the database function
 */
export async function removeCustomerFromProject(
  projectId: string,
  customerId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('remove_customer_from_project', {
    p_project_id: projectId,
    p_customer_id: customerId,
  });

  if (error) {
    // Check if it's the "last customer" error
    if (error.message.includes('Cannot delete the last customer')) {
      throw new Error('Cannot remove the last customer from a project. Every project must have at least one customer.');
    }
    console.error('[projectCustomersApi] Error removing customer from project:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Get available customers for a project (customers not already on the project)
 */
export async function fetchAvailableCustomers(
  businessId: string,
  projectId: string
): Promise<Array<{ id: string; first_name: string; last_name: string; email: string | null }>> {
  // Get customers already on the project
  const { data: existingCustomers, error: existingError } = await supabase
    .from('project_customers')
    .select('customer_id')
    .eq('project_id', projectId);

  if (existingError) {
    console.error('[projectCustomersApi] Error fetching existing customers:', existingError);
    throw new Error(existingError.message);
  }

  const existingIds = (existingCustomers || []).map((c) => c.customer_id);

  // Get all customers for the business excluding those already on the project
  let query = supabase
    .from('customers')
    .select('id, first_name, last_name, email')
    .eq('business_id', businessId)
    .order('last_name', { ascending: true });

  if (existingIds.length > 0) {
    query = query.not('id', 'in', `(${existingIds.join(',')})`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[projectCustomersApi] Error fetching available customers:', error);
    throw new Error(error.message);
  }

  return data || [];
}
